/* eslint-disable no-plusplus */
/* eslint-disable no-alert */
import EmptyResult from 'components/atoms/empty_result';
import Loader from 'components/atoms/loader';
import UsagePolicy from 'components/atoms/usage_policy';
import BulletinBiddings from 'components/organisms/bulletin_biddings';
import BulletinFollowups from 'components/organisms/bulletin_followups';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { sendInlineBulletin } from 'services/actions/bulletins';
import createTrack from 'services/actions/tracker_actions';
import { track } from 'services/tracking';
import { captalizeFirstLetter } from 'services/captalize';
import tabId from 'services/tabId';
import { ApplicationState } from 'store';
import {
  fetchBulletinBiddings,
  fetchBulletinContentById,
  fetchBulletinFollowups,
  fetchBulletins,
  getOverview,
} from 'store/modules/bulletins_store/actions';
import {
  BulletinFilterTypes,
  BulletinsState,
  RequestTypes,
} from 'store/modules/bulletins_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import OverviewBulletin from 'components/organisms/overview_bulletin';

interface BulletinContentParams {
  id?: string;
}

const BulletinContent: React.FC = () => {
  const { id = '' } = useParams<BulletinContentParams>();

  const PAGE_SIZE = 50;

  const { bulletins, request } = useSelector<ApplicationState, BulletinsState>(
    (state) => state.session.bulletins,
  );

  const currentUrl = window.location.href;

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const representedCompanyId =
    profile.representant && profile.currentCompany[tabId()] !== profile.company_data.data.id
      ? profile.currentCompany[tabId()]
      : undefined;

  const [bulletinFilterIndex, setBulletinFilterIndex] = useState<number>(-1);
  const [bulletinFilter, setBulletinFilter] = useState<BulletinFilterTypes | null>(null);
  const [biddingEnabled, setBiddingEnabled] = useState<boolean>(true);
  const [overview, setOverview] = useState<boolean>(false);
  const [followupEnabled, setFollowupEnabled] = useState<boolean>(false);
  const [selectedBiddings, setSelectedBiddings] = useState<Array<number>>([]);
  const [selectedFollowups, setSelectedFollowups] = useState<Array<number>>([]);
  const [wholeBulletinSelected, setWholeBulletinSelected] = useState<boolean>(false);
  const [requestCurrentBulletinBiddingPage, setRequestCurrentBulletinBiddingPage] =
    useState<RequestTypes>({
      loading: false,
      loaded: false,
      error: false,
    });
  const [requestCurrentBulletinFollowupPage, setRequestCurrentBulletinFollowupPage] =
    useState<RequestTypes>({
      loading: false,
      loaded: false,
      error: false,
    });

  const dispatch = useDispatch();

  useEffect(() => {
    document.title = 'Boletim - ConLicitação';
  }, []);

  const findBulletinFilterIndex = () => {
    return bulletins?.filter_bulletins?.findIndex(
      (filter_bulletin) =>
        filter_bulletin?.bulletin_filters
          .filter((bulletin_filter) => bulletin_filter?.id.toString() === id)[0]
          ?.id.toString() === id,
    );
  };

  useEffect(() => {
    if (
      !request?.fetch_bulletin_biddings[
        `bulletin_${id}_page_${bulletins?.bulletins_biddings_list[id]?.current_page || 1}`
      ]
    )
      return;

    setRequestCurrentBulletinBiddingPage(
      request?.fetch_bulletin_biddings[
        `bulletin_${id}_page_${bulletins?.bulletins_biddings_list[id]?.current_page || 1}`
      ],
    );
  }, [request?.fetch_bulletin_biddings, bulletins?.bulletins_biddings_list[id]?.current_page]);

  useEffect(() => {
    setRequestCurrentBulletinFollowupPage(
      request?.fetch_bulletin_followups[
        `bulletin_${id}_page_${bulletins?.bulletins_followups_list[id]?.current_page}`
      ],
    );
  }, [
    request?.fetch_bulletin_followups[
      `bulletin_${id}_page_${bulletins?.bulletins_followups_list[id]?.current_page}`
    ],
  ]);

  useEffect(() => {
    if (profile.representant && profile.currentCompany[tabId()] !== profile.company_data.data.id) {
      dispatch(
        fetchBulletinBiddings({
          bulletin_id: id,
          companyId: profile.currentCompany[tabId()],
        }),
      );
      return;
    }

    if (requestCurrentBulletinBiddingPage.loaded || requestCurrentBulletinBiddingPage.loading)
      return;

    dispatch(fetchBulletinBiddings({ bulletin_id: id }));

    if (requestCurrentBulletinFollowupPage.loaded || requestCurrentBulletinFollowupPage.loading)
      return;

    dispatch(fetchBulletinFollowups(id));
    dispatch(getOverview(id));
  }, []);

  useEffect(() => {
    if (!request?.fetch_bulletin_filters?.loaded) return;

    dispatch(fetchBulletinContentById(Number(id)));
  }, [request.fetch_bulletin_filters]);

  const abriuTrackedIssueRef = useRef<string | null>(null);
  useEffect(() => {
    if (!request?.fetch_bulletin_filters?.loaded) return;
    createTrack('Abriu Boletim Web', {
      origin: currentUrl.includes('open') ? 'Email' : 'Site',
      filter: bulletins?.filter_bulletins[bulletinFilterIndex]?.filter.description,
      issue: bulletinFilter?.issue.toString(),
    });
    // dispara uma vez por edição: bulletinFilter é reatribuído várias vezes no load,
    // o que fazia boletim.abriu duplicar (rajada de ms). Guard por issue mata isso.
    const issue = bulletinFilter?.issue?.toString() ?? null;
    if (issue && abriuTrackedIssueRef.current !== issue) {
      abriuTrackedIssueRef.current = issue;
      track('boletim.abriu_boletim', {
        origem: currentUrl.includes('open') ? 'email' : 'site',
        issue,
      });
    }
  }, [bulletinFilter]);

  useEffect(() => {
    if (!request?.fetch_bulletin_content_by_id?.loaded) return;

    setBulletinFilterIndex(findBulletinFilterIndex());
  }, [request.fetch_bulletin_filters, bulletins.filter_bulletins]);

  useEffect(() => {
    const index = findBulletinFilterIndex();

    if (index === -1 && !request.fetch_bulletin_filters.loaded) dispatch(fetchBulletins());

    if (index) setBulletinFilterIndex(index);
  }, []);

  useEffect(() => {
    setBulletinFilter(
      bulletins?.filter_bulletins[bulletinFilterIndex]?.bulletin_filters?.filter(
        (bulletin_filter) => bulletin_filter?.id?.toString() === id,
      )[0],
    );
  }, [bulletinFilterIndex]);

  useEffect(() => {
    setSelectedBiddings([]);
    setSelectedFollowups([]);
  }, [followupEnabled]);

  const handleBiddingOrFollowup = () => {
    setBiddingEnabled(!biddingEnabled);
    setFollowupEnabled(!followupEnabled);
  };

  useEffect(() => {
    if (
      selectedBiddings.length === bulletins?.bulletins_biddings_list[id]?.total_entries &&
      selectedFollowups.length === bulletins?.bulletins_followups_list[id]?.total_entries
    ) {
      setWholeBulletinSelected(true);
    } else {
      setWholeBulletinSelected(false);
    }
  }, [selectedBiddings, selectedFollowups]);

  useEffect(() => {
    if (
      !request.fetch_bulletin_biddings[
        `bulletin_${id}_page_${bulletins?.bulletins_biddings_list[id]?.current_page}`
      ]
    )
      dispatch(
        fetchBulletinBiddings({
          bulletin_id: id.toString(),
          page: bulletins?.bulletins_biddings_list[id]?.current_page,
          per_page: PAGE_SIZE,
          companyId: representedCompanyId,
        }),
      );

    if (requestCurrentBulletinBiddingPage?.loading) return;

    const index = ((bulletins?.bulletins_biddings_list[id]?.current_page || 1) - 1) * PAGE_SIZE;

    if (!bulletins.bulletins_biddings_list[id]) return;

    if (bulletins.bulletins_biddings_list[id].biddings[index]?.id > 0) return;

    dispatch(
      fetchBulletinBiddings({
        bulletin_id: id.toString(),
        page: bulletins?.bulletins_biddings_list[id]?.current_page,
        per_page: PAGE_SIZE,
        companyId: representedCompanyId,
      }),
    );
  }, [bulletins?.bulletins_biddings_list[id]?.current_page]);

  useEffect(() => {
    if (
      !request.fetch_bulletin_followups[
        `bulletin_${id}_page_${bulletins?.bulletins_followups_list[id]?.current_page}`
      ]
    )
      dispatch(
        fetchBulletinFollowups(
          id.toString(),
          bulletins?.bulletins_followups_list[id]?.current_page,
          PAGE_SIZE,
        ),
      );

    if (requestCurrentBulletinFollowupPage?.loading) return;

    const index = ((bulletins?.bulletins_followups_list[id]?.current_page || 1) - 1) * PAGE_SIZE;

    if (!bulletins.bulletins_followups_list[id]) return;

    if (bulletins.bulletins_followups_list[id].followups[index]?.id > 0) return;

    dispatch(
      fetchBulletinFollowups(
        id.toString(),
        bulletins?.bulletins_followups_list[id]?.current_page,
        PAGE_SIZE,
      ),
    );
  }, [bulletins?.bulletins_followups_list[id]?.current_page]);

  useEffect(() => {
    if (
      request?.fetch_bulletin_biddings[
        `bulletin_${id}_page_${bulletins?.bulletins_biddings_list[id]?.current_page || 1}`
      ]?.loading
    )
      return;

    const pages = Array.from(
      { length: bulletins.bulletins_biddings_list[id]?.total_pages },
      (_, i) => i + 1,
    );

    const loadingPage = pages.find(
      (page) => request.fetch_bulletin_biddings[`bulletin_${id}_page_${page}`]?.loading,
    );

    if (loadingPage) return;

    const nextPage = pages.find(
      (page) =>
        !request.fetch_bulletin_biddings[`bulletin_${id}_page_${page}`] ||
        request.fetch_bulletin_biddings[`bulletin_${id}_page_${page}`].error,
    );

    if (nextPage)
      dispatch(
        fetchBulletinBiddings({
          bulletin_id: id,
          page: nextPage,
          per_page: PAGE_SIZE,
          passive_load: true,
          companyId: representedCompanyId,
        }),
      );
  }, [request]);

  useEffect(() => {
    const currentPage = bulletins?.bulletins_followups_list[id]?.current_page;

    for (
      let index = currentPage;
      index <= bulletins?.bulletins_followups_list[id]?.total_pages;
      index++
    ) {
      const nextPage = index + 1;
      if (nextPage > bulletins?.bulletins_followups_list[id].total_pages) break;

      if (request.fetch_bulletin_followups[`bulletin_${id}_page_${nextPage}`]?.loaded) return;
      if (request.fetch_bulletin_followups[`bulletin_${id}_page_${nextPage}`]?.loading) return;

      dispatch(fetchBulletinFollowups(id, nextPage, PAGE_SIZE, true));
    }
  }, [requestCurrentBulletinFollowupPage]);

  const toogleSelectWholeBulletin = () => {
    if (
      selectedBiddings.length === bulletins?.bulletins_biddings_list[id]?.total_entries &&
      selectedFollowups.length === bulletins?.bulletins_followups_list[id]?.total_entries
    ) {
      setSelectedBiddings([]);
      setSelectedFollowups([]);
      return;
    }

    setSelectedBiddings(bulletins?.bulletins_biddings_list[id]?.biddings_ids);
    setSelectedFollowups(bulletins?.bulletins_followups_list[id]?.followups_ids);
  };

  const sendEmailInlineBulletin = async () => {
    const response_status = await sendInlineBulletin(bulletinFilter?.id || 0);

    if (response_status === 200) alert('Email enviado com sucesso!');
    else if (response_status === 202) {
      alert('Pelo volume de itens, o boletim vai em anexo. Enviaremos em alguns minutos.');
    } else alert('Erro ao enviar o email. Tente Novamente.');
  };

  const calendarUrl = () => {
    if (bulletinFilter?.filter_id && bulletinFilter?.bulletin.closing_date)
      return `/boletim_web/public/boletins?filter_id=${bulletinFilter?.filter_id}&month=${bulletinFilter?.bulletin.closing_date}`;

    if (bulletinFilter?.filter_id)
      return `/boletim_web/public/boletins?filter_id=${bulletinFilter?.filter_id}`;

    if (bulletinFilter?.bulletin.closing_date)
      return `/boletim_web/public/boletins?month=${bulletinFilter?.bulletin.closing_date}`;

    return '/boletim_web/public/boletins';
  };

  const bulletinContentNotStarted =
    !request?.fetch_bulletin_content_by_id?.loading &&
    !request?.fetch_bulletin_content_by_id?.loaded &&
    !request?.fetch_bulletin_content_by_id?.error &&
    !request?.fetch_bulletin_filters?.error &&
    !requestCurrentBulletinBiddingPage?.error &&
    !requestCurrentBulletinFollowupPage?.error;

  return (
    <div className="page-container">
      <div className="container p-4">
        {(requestCurrentBulletinBiddingPage?.error ||
          requestCurrentBulletinFollowupPage?.error ||
          request?.fetch_bulletin_content_by_id?.error ||
          request?.fetch_bulletin_filters?.error) && (
          <EmptyResult title="Desculpe!" description="Algo deu errado, tente novamente." />
        )}
        {(requestCurrentBulletinBiddingPage?.loading ||
          !requestCurrentBulletinBiddingPage ||
          requestCurrentBulletinFollowupPage?.loading ||
          !requestCurrentBulletinFollowupPage ||
          request?.fetch_bulletin_content_by_id?.loading ||
          bulletinContentNotStarted ||
          (!requestCurrentBulletinBiddingPage?.loading &&
            !requestCurrentBulletinBiddingPage?.loaded &&
            !requestCurrentBulletinBiddingPage?.error)) && <Loader />}
        {requestCurrentBulletinBiddingPage?.loaded &&
          requestCurrentBulletinFollowupPage?.loaded &&
          request?.fetch_bulletin_content_by_id?.loaded && (
            <>
              <div className="row">
                <div className="col-6 px-0 mb-3 d-print-none">
                  <Link to={calendarUrl()}>
                    <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar para
                    boletins
                  </Link>
                </div>
                <div className="col-6 px-0 mb-3 text-right d-print-none">
                  <a
                    className={!bulletinFilter?.previous_bulletin_filter_id ? 'btn disabled' : ''}
                    href={`/boletim_web/public/boletins/${bulletinFilter?.previous_bulletin_filter_id}`}
                  >
                    <i className="material-icons align-middle">keyboard_arrow_left</i> Anterior
                  </a>
                  <span className="px-1">|</span>
                  <a
                    className={!bulletinFilter?.next_bulletin_filter_id ? 'btn disabled' : ''}
                    href={`/boletim_web/public/boletins/${bulletinFilter?.next_bulletin_filter_id}`}
                  >
                    Próximo <i className="material-icons align-middle">keyboard_arrow_right</i>
                  </a>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="card p-3 rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4>
                          Boletim {moment(bulletinFilter?.bulletin.closing_date).format('DD')} de{' '}
                          {captalizeFirstLetter(
                            moment(bulletinFilter?.bulletin.closing_date).format('MMMM , HH:MM'),
                          )}{' '}
                          - Edição nº: <b>{bulletinFilter?.issue}</b>
                        </h4>
                        <h6>
                          Filtro:{' '}
                          <b>
                            {bulletins.filter_bulletins &&
                              bulletins?.filter_bulletins[bulletinFilterIndex]?.filter?.description}
                          </b>
                        </h6>
                      </div>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-primary dropdown-toggle d-flex align-items-center"
                          onClick={() => {
                            setOverview((prev) => !prev);
                            createTrack('Boletim - Clicou botão resumo do boletim');
                          }}
                          style={{ gap: 8 }}
                          disabled={bulletins?.bulletins_biddings_list[id]?.total_entries === 0}
                        >
                          <span>Resumo do Boletim</span>
                        </button>
                      </div>
                    </div>
                    {overview && (
                      <>
                        <hr className="mb-0" />
                        {request?.overview?.loading ? <Loader /> : <OverviewBulletin />}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mb-4 d-print-none mt-3">
                <div className="col-12 d-flex">
                  <button
                    className={`mr-1 btn btn-sm ${
                      biddingEnabled ? 'btn-primary' : 'btn-outline-primary'
                    }`}
                    type="button"
                    onClick={() => {
                      if (biddingEnabled) return;
                      handleBiddingOrFollowup();
                    }}
                  >
                    Licitações{' '}
                    <span className="badge-btn-bulletin badge">
                      {bulletins?.bulletins_biddings_list[id]?.total_entries}
                    </span>
                  </button>
                  <button
                    className={`ml-1 btn btn-sm ${
                      followupEnabled ? 'btn-primary' : 'btn-outline-primary'
                    }`}
                    type="button"
                    onClick={() => {
                      if (followupEnabled) return;
                      handleBiddingOrFollowup();
                    }}
                  >
                    Acompanhamentos{' '}
                    <span className="badge-btn-bulletin badge">
                      {bulletins?.bulletins_followups_list[id]?.total_entries}
                    </span>
                  </button>
                  {profile.consultant_login && (
                    <button
                      className="ml-auto btn btn-sm btn-success"
                      type="button"
                      onClick={() => sendEmailInlineBulletin()}
                    >
                      Enviar por Email{' '}
                    </button>
                  )}
                </div>
              </div>
              {/* <div className={!followupEnabled ? 'd-block' : 'd-none'}> */}
              {!followupEnabled && (
                <BulletinBiddings
                  bulletin_filter_id={Number(id)}
                  bulletinFilterIndex={bulletinFilterIndex}
                  selectedBiddings={selectedBiddings}
                  setSelectedBiddings={setSelectedBiddings}
                  setSelectedFollowups={setSelectedFollowups}
                  selectedFollowups={selectedFollowups}
                  toogleSelectWholeBulletin={toogleSelectWholeBulletin}
                  wholeBulletinSelected={wholeBulletinSelected}
                />
              )}
              {/* </div> */}

              {/* <div className={followupEnabled ? 'd-block' : 'd-none'}> */}
              {followupEnabled && (
                <BulletinFollowups
                  selectedBiddings={selectedBiddings}
                  bulletinFilterIndex={bulletinFilterIndex}
                  bulletin_filter_id={Number(id)}
                  selectedFollowups={selectedFollowups}
                  setSelectedFollowups={setSelectedFollowups}
                  setSelectedBiddings={setSelectedBiddings}
                  toogleSelectWholeBulletin={toogleSelectWholeBulletin}
                  wholeBulletinSelected={wholeBulletinSelected}
                />
              )}
              {/* </div> */}
              <div className="row">
                <div className="col-6 px-0 mb-3 d-print-none">
                  <Link
                    to={`/boletim_web/public/boletins?filter_id=${bulletinFilter?.filter_id}&month=${bulletinFilter?.bulletin.closing_date}`}
                  >
                    <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar para
                    boletins
                  </Link>
                </div>
                <div className="col-6 px-0 mb-3 text-right d-print-none">
                  <a
                    className={!bulletinFilter?.previous_bulletin_filter_id ? 'btn disabled' : ''}
                    href={`/boletim_web/public/boletins/${bulletinFilter?.previous_bulletin_filter_id}`}
                  >
                    <i className="material-icons align-middle">keyboard_arrow_left</i> Anterior
                  </a>
                  <span className="px-1">|</span>
                  <a
                    className={!bulletinFilter?.next_bulletin_filter_id ? 'btn disabled' : ''}
                    href={`/boletim_web/public/boletins/${bulletinFilter?.next_bulletin_filter_id}`}
                  >
                    Próximo <i className="material-icons align-middle">keyboard_arrow_right</i>
                  </a>
                </div>
              </div>
              <div className="row mb-5">
                <div className="col-md-12 mb-3 text-right d-print-none">
                  <button
                    className="btn btn-secondary btn-sm pl-3"
                    onClick={() =>
                      document.querySelector('#windowDiv')?.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    type="button"
                  >
                    Voltar para o topo{' '}
                    <span className="material-icons align-middle">arrow_drop_up</span>
                  </button>
                </div>
              </div>
              <div className="row">
                <UsagePolicy />
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default BulletinContent;
